import { useOrgContext } from '@/org/contexts/OrgContext'
import {
  Meeting_Step_Type_Enum,
  NextStepFragment,
  useCircleNextStepsSubscription,
} from '@gql'
import React, { createContext, useContext, useMemo } from 'react'
import { isThreadsStep } from '../components/meetingStepThreadTypes'
import { MeetingContext } from './MeetingContext'

// The next steps of the meeting's circle, subscribed ONCE per meeting (#42,
// ENR-08). The tensions steps (under each tension) and the Next steps board
// both need the very same list; each used to open its own subscription with
// identical variables, which doubled the traffic and the memory for nothing.
// Opened only when the meeting has one of those steps.
interface MeetingNextSteps {
  nextSteps: NextStepFragment[] | undefined
}

// null = outside a meeting: the component subscribes on its own
const MeetingNextStepsContext = createContext<MeetingNextSteps | null>(null)

export function useMeetingNextSteps(): MeetingNextSteps | null {
  return useContext(MeetingNextStepsContext)
}

export function MeetingNextStepsProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const { steps, circle } = useContext(MeetingContext)!
  const { orgData } = useOrgContext()

  const needed = !!steps?.some(
    (step) =>
      isThreadsStep(step.type) || step.type === Meeting_Step_Type_Enum.NextSteps
  )
  // The board's scope: see OrgData.circleWithChildren
  const circlesIds = useMemo(
    () =>
      circle
        ? (orgData?.circleWithChildren(circle.id) ?? []).map((c) => c.id)
        : [],
    [orgData, circle]
  )
  const { data } = useCircleNextStepsSubscription({
    skip: !needed || circlesIds.length === 0,
    variables: { circlesIds },
  })

  const value = useMemo(
    () => ({ nextSteps: data?.next_step }),
    [data?.next_step]
  )
  return (
    <MeetingNextStepsContext.Provider value={value}>
      {children}
    </MeetingNextStepsContext.Provider>
  )
}
